import { useQuery } from "@tanstack/react-query";
import { fetchCandidateCount, fetchEntities, fetchRenewals } from "./dataFetchers";
import { RENEWAL_DUE_SOON_DAYS } from "./severity";

export interface NavCounts {
  entities: number;
  renewals: number;
  regwatch: number;
}

/**
 * Returns live entity, renewal, and regulatory-watch counts derived from the
 * react-query cache. Uses the same query keys as the pages that own each feed
 * (Home, Renewals, Regulatory Watch) so the cache is shared — no extra network
 * requests when those pages are already mounted.
 */
export function useNavCounts(): NavCounts {
  const { data: entitiesRes } = useQuery({
    queryKey: ["entities-all"],
    queryFn: () => fetchEntities({ limit: 100 }),
    staleTime: 60_000,
  });
  const { data: renewals } = useQuery({
    queryKey: ["renewals"],
    queryFn: () => fetchRenewals(),
    staleTime: 60_000,
  });
  const { data: regwatch } = useQuery({
    queryKey: ["regulatory-count"],
    queryFn: () => fetchCandidateCount(),
    staleTime: 60_000,
  });

  return {
    entities: entitiesRes?.entities.length ?? entitiesRes?.total ?? 0,
    // Renewals badge counts urgent + past items (severity === "red" or
    // daysUntil ≤ due-soon) — matches the Home CRITICAL KPI.
    renewals: (renewals ?? []).filter(
      (r) => r.severity === "red" || r.daysUntil <= RENEWAL_DUE_SOON_DAYS,
    ).length,
    // Regulatory Watch badge counts all open alerts (global feed count).
    regwatch: regwatch ?? 0,
  };
}
